import Phaser from "phaser";
import { APEX_RANGE, CATEGORY_RANGES, ELITE_RANGE } from "../config";
import type { EnemyCategory, EnemyTier, RegionDef } from "../types";

/** One enemy to place on the map at mission start. */
export interface Placement {
  x: number;
  y: number;
  strength: number;
  tier: EnemyTier;
  /** Visual band for standard enemies (ignored for elite/apex). */
  category: EnemyCategory;
}

const EDGE_MARGIN = 90;
/** Keep the area around the player's start clear so they aren't spawned-on. */
const SAFE_RADIUS = 240;
const MIN_SPACING = 110;

/**
 * Builds the full, fixed enemy population for a region. Everything is placed at
 * mission start; the population only ever decreases. Strengths are relative to
 * the player's STARTING strength (the map is static, so they never re-scale).
 * Apex enemies are spread across the map's quadrants, never clustered.
 */
export function buildPopulation(region: RegionDef, startStrength: number): Placement[] {
  const { width, height } = region.map;
  const cx = width / 2;
  const cy = height / 2;
  const placed: Placement[] = [];

  const roll = (r: { min: number; max: number }) =>
    Math.max(1, Math.round(startStrength * Phaser.Math.FloatBetween(r.min, r.max)));

  const farEnough = (x: number, y: number): boolean => {
    if (Phaser.Math.Distance.Between(x, y, cx, cy) < SAFE_RADIUS) return false;
    return placed.every((p) => Phaser.Math.Distance.Between(x, y, p.x, p.y) >= MIN_SPACING);
  };

  const randomSpot = (bounds?: Phaser.Geom.Rectangle): { x: number; y: number } => {
    const bx = bounds?.x ?? EDGE_MARGIN;
    const by = bounds?.y ?? EDGE_MARGIN;
    const bw = bounds?.width ?? width - EDGE_MARGIN * 2;
    const bh = bounds?.height ?? height - EDGE_MARGIN * 2;
    for (let i = 0; i < 40; i++) {
      const x = Phaser.Math.Between(bx, bx + bw);
      const y = Phaser.Math.Between(by, by + bh);
      if (farEnough(x, y)) return { x, y };
    }
    // Fallback: accept a spot even if a little close, to guarantee the count.
    return { x: Phaser.Math.Between(bx, bx + bw), y: Phaser.Math.Between(by, by + bh) };
  };

  const add = (count: number, tier: EnemyTier, category: EnemyCategory, range: { min: number; max: number }) => {
    for (let i = 0; i < count; i++) {
      const { x, y } = randomSpot();
      placed.push({ x, y, strength: roll(range), tier, category });
    }
  };

  // Standard enemies by band, plus elites.
  add(region.weak, "standard", "weak", CATEGORY_RANGES.weak);
  add(region.matched, "standard", "matched", CATEGORY_RANGES.matched);
  add(region.threat, "standard", "threat", CATEGORY_RANGES.threat);
  add(region.elite, "elite", "threat", ELITE_RANGE);

  // Apex: spread one across each map quadrant (round-robin) so they're never
  // clustered. The category is nominal — apex uses its own visual.
  const quads = [
    new Phaser.Geom.Rectangle(EDGE_MARGIN, EDGE_MARGIN, width / 2 - EDGE_MARGIN, height / 2 - EDGE_MARGIN),
    new Phaser.Geom.Rectangle(width / 2, EDGE_MARGIN, width / 2 - EDGE_MARGIN, height / 2 - EDGE_MARGIN),
    new Phaser.Geom.Rectangle(EDGE_MARGIN, height / 2, width / 2 - EDGE_MARGIN, height / 2 - EDGE_MARGIN),
    new Phaser.Geom.Rectangle(width / 2, height / 2, width / 2 - EDGE_MARGIN, height / 2 - EDGE_MARGIN),
  ];
  for (let i = 0; i < region.apex; i++) {
    const { x, y } = randomSpot(quads[i % quads.length]);
    placed.push({ x, y, strength: roll(APEX_RANGE), tier: "apex", category: "threat" });
  }

  return placed;
}
